import { CSSProperties, ReactNode, useMemo } from 'react'
import {
  FONT_CQW,
  FORMAT_ASPECT_RATIO,
  FORMAT_DIMENSIONS,
  SIZE_SCALE,
} from './shareCardTokens'
import { INK_GROUND, SPLIT_RAIL_GROUND, resolveInk } from './shareCardContrast'
import { ShareCardProps } from './ShareCard.types'
import logoMark from '../../../../assets/50px_logo.svg'
import wordmarkWhite from '../../../../assets/purposely_wordmark_white_320.png'
import wordmarkBlue from '../../../../assets/purposely_wordmark_blue_320.png'

/** A layout token (padding, gap, widths, secondary text) in container-width units. */
const cqw = (value: number) => `${value}cqw`

/**
 * A font-size token with the S/M/L multiplier applied. Per the design spec this is used
 * only for the headline and the stat number; everything else uses `cqw` unscaled.
 */
const scaledCqw = (value: number, size: ShareCardProps['size']) => `${value * SIZE_SCALE[size]}cqw`

/** Was the requested ink swapped for a legible fallback? Surfaced by the modal as a warning. */
export function useShareCardInk(layout: ShareCardProps['layout'], color: string) {
  return useMemo(() => {
    if (layout === 'split') {
      // Split has two grounds: the left rail (stat) sits on evergreen, the right
      // column (headline) sits on white. A color that fails either one falls back.
      const rail = resolveInk(layout, color, SPLIT_RAIL_GROUND)
      const headline = resolveInk(layout, color, INK_GROUND.split)
      return {
        railInk: rail.ink,
        headlineInk: headline.ink,
        fellBack: rail.fellBack || headline.fellBack,
      }
    }
    const resolved = resolveInk(layout, color)
    return { railInk: resolved.ink, headlineInk: resolved.ink, fellBack: resolved.fellBack }
  }, [layout, color])
}

/**
 * The card's outer box. It establishes the container that every `cqw` value inside is
 * measured against. Container units on an element resolve against its nearest *ancestor*
 * container, so the padded/laid-out surface has to be a child of this element rather
 * than the element itself, or the root padding would fall back to viewport units.
 */
const CardFrame = ({ format, style, children }: { format: ShareCardProps['format']; style: CSSProperties; children: ReactNode }) => (
  <div style={{ containerType: 'inline-size', aspectRatio: FORMAT_ASPECT_RATIO[format], width: '100%', position: 'relative', overflow: 'hidden' }}>
    <div style={{ position: 'absolute', inset: 0, boxSizing: 'border-box', display: 'flex', ...style }}>{children}</div>
  </div>
)

const Footer = ({ layout, linkColor, color }: { layout: ShareCardProps['layout']; linkColor: string; color: string }) => (
  <span style={{ fontSize: cqw(FONT_CQW.footer[layout]), color }}>
    Powered by{' '}
    <a href="https://purposely.ai" style={{ color: linkColor, fontWeight: 600, textDecoration: 'none' }}>
      Purposely.ai — Try it free
    </a>
  </span>
)

const BrandLine = ({ layout, brandLine, style }: { layout: ShareCardProps['layout']; brandLine: string; style?: CSSProperties }) => (
  <div style={{ borderTop: '1px solid #e0e0e0', color: '#4e4e4e', fontSize: cqw(FONT_CQW.brandLine[layout]), ...style }}>
    Insight from <strong style={{ color: '#051E33' }}>{brandLine}</strong>
  </div>
)

const paragraph: CSSProperties = { lineHeight: 1.5, margin: 0, textWrap: 'pretty' as CSSProperties['textWrap'] }

const StatLayout = ({ format, size, headline, labelType, stat, sentence, brandLine, headlineInk }: ShareCardProps & { headlineInk: string }) => (
  <CardFrame format={format} style={{ background: '#003673', padding: `${cqw(4.67)} ${cqw(6)}`, flexDirection: 'column', justifyContent: 'space-between' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      {/* The full-color mark on a white chip, matching brand usage on dark grounds. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: cqw(1.2), background: '#fff', borderRadius: 6, padding: `${cqw(1)} ${cqw(1.4)}` }}>
        <img src={logoMark} alt="" style={{ width: cqw(3.2), height: cqw(3.2) }} />
        <img src={wordmarkBlue} alt="Purposely" style={{ height: cqw(2.2) }} />
      </div>
      <span style={{ fontSize: cqw(1.25), letterSpacing: '0.16em', color: '#FFDABB', textTransform: 'uppercase' }}>
        Creative Insight
      </span>
    </div>

    <div style={{ display: 'flex', alignItems: 'flex-end', gap: cqw(4) }}>
      <div
        style={{
          fontFamily: 'Roobert',
          fontWeight: 600,
          lineHeight: 0.82,
          letterSpacing: '-0.04em',
          color: headlineInk,
          fontSize: scaledCqw(FONT_CQW.statNumber.stat, size),
        }}
      >
        {stat}
      </div>
      <div style={{ maxWidth: cqw(43), minWidth: cqw(24), display: 'flex', flexDirection: 'column', gap: cqw(1.4) }}>
        <div style={{ fontFamily: 'Roobert', fontWeight: 500, lineHeight: 1.15, color: headlineInk, fontSize: scaledCqw(FONT_CQW.headline.stat, size) }}>
          {headline}
        </div>
        <span style={{ fontSize: cqw(FONT_CQW.labelEyebrow.stat), letterSpacing: '0.18em', textTransform: 'uppercase', color: '#FFDABB' }}>
          {labelType}
        </span>
        <p style={{ ...paragraph, color: '#fff', fontSize: cqw(FONT_CQW.sentence.stat) }}>{sentence}</p>
        <span style={{ color: '#fff', fontSize: cqw(FONT_CQW.sentence.stat) }}>
          for <strong>{brandLine}</strong>
        </span>
      </div>
    </div>

    <div style={{ borderTop: '1px solid rgba(255,255,255,0.25)', paddingTop: cqw(2.2) }}>
      <Footer layout="stat" linkColor="#FFDABB" color="#fff" />
    </div>
  </CardFrame>
)

const SplitLayout = ({ format, size, headline, labelType, stat, sentence, brandLine, railInk, headlineInk }: ShareCardProps & { railInk: string; headlineInk: string }) => (
  <CardFrame format={format} style={{ background: '#fff', flexDirection: 'row' }}>
    <div
      style={{
        width: cqw(39.2),
        flexShrink: 0,
        background: '#0B6D5E',
        color: '#fff',
        padding: `${cqw(4.67)} ${cqw(4)}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
      }}
    >
      <img src={wordmarkWhite} alt="Purposely" style={{ height: cqw(2), alignSelf: 'flex-start' }} />
      <div>
        <span style={{ fontSize: cqw(FONT_CQW.labelEyebrow.split), letterSpacing: '0.18em', textTransform: 'uppercase', color: '#FFDABB' }}>
          Label Lift
        </span>
        <div
          style={{
            fontFamily: 'Roobert',
            fontWeight: 600,
            lineHeight: 0.85,
            letterSpacing: '-0.03em',
            color: railInk,
            fontSize: scaledCqw(FONT_CQW.statNumber.split, size),
          }}
        >
          {stat}
        </div>
      </div>
      <Footer layout="split" linkColor="#FFDABB" color="#fff" />
    </div>
    <div style={{ flex: 1, minWidth: 0, padding: `${cqw(5.3)} ${cqw(5)}`, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: cqw(1.6) }}>
      <div style={{ fontFamily: 'Roobert', fontWeight: 500, lineHeight: 1.15, color: headlineInk, fontSize: scaledCqw(FONT_CQW.headline.split, size) }}>
        {headline}
      </div>
      <span style={{ fontSize: cqw(FONT_CQW.labelEyebrow.split), letterSpacing: '0.18em', textTransform: 'uppercase', color: '#0B6D5E' }}>
        {labelType}
      </span>
      <p style={{ ...paragraph, color: '#051E33', fontSize: cqw(FONT_CQW.sentence.split) }}>{sentence}</p>
      <BrandLine layout="split" brandLine={brandLine} style={{ marginTop: 'auto', paddingTop: cqw(1.6) }} />
    </div>
  </CardFrame>
)

const PlaqueLayout = ({ format, size, headline, labelType, stat, sentence, brandLine, headlineInk }: ShareCardProps & { headlineInk: string }) => {
  // Plaque stacks five text blocks vertically. At Wide's 630px height (vs. 1080-1350 for
  // square/portrait) the spec's padding and gap don't fit once the stat number is large,
  // so the vertical spacing is compressed for the short format only. This is an
  // engineering call rather than a spec value.
  const isShort = FORMAT_DIMENSIONS[format].height / FORMAT_DIMENSIONS[format].width < 0.75
  const density = isShort ? 0.6 : 1
  return (
    <CardFrame format={format} style={{ background: '#FFDABB', padding: cqw(3.33 * density), flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: cqw(1), padding: `0 ${cqw(2)} ${cqw(2.2 * density)} ${cqw(2)}` }}>
        <img src={logoMark} alt="" style={{ width: cqw(2.8), height: cqw(2.8) }} />
        <img src={wordmarkBlue} alt="Purposely" style={{ height: cqw(1.9) }} />
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          background: '#fff',
          borderRadius: 6,
          padding: `${cqw(4.6 * density)} ${cqw(6.6)}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: cqw(1.8 * density),
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <div style={{ fontFamily: 'Roobert', fontWeight: 500, lineHeight: 1.15, color: headlineInk, fontSize: scaledCqw(FONT_CQW.headline.plaque, size) }}>
          {headline}
        </div>
        <span style={{ fontSize: cqw(FONT_CQW.labelEyebrow.plaque), letterSpacing: '0.2em', textTransform: 'uppercase', color: '#888888' }}>
          {labelType}
        </span>
        <div style={{ fontFamily: 'Roobert', fontWeight: 600, color: headlineInk, fontSize: scaledCqw(FONT_CQW.statNumber.plaque, size) }}>
          {stat}
        </div>
        <p style={{ ...paragraph, color: '#051E33', maxWidth: cqw(63), fontSize: cqw(FONT_CQW.sentence.plaque) }}>{sentence}</p>
        <BrandLine layout="plaque" brandLine={brandLine} style={{ width: '100%', paddingTop: cqw(1.4 * density) }} />
      </div>
      <div style={{ textAlign: 'center', paddingTop: cqw(2.2 * density) }}>
        <Footer layout="plaque" linkColor="#0B6D5E" color="#4e4e4e" />
      </div>
    </CardFrame>
  )
}

/**
 * One presentational component, three layouts × three aspect ratios. Fully driven by
 * props; renders both the on-screen live preview and, off-screen at full size, the PNG.
 */
export const ShareCard = (props: ShareCardProps) => {
  const { layout } = props
  const { railInk, headlineInk } = useShareCardInk(layout, props.color)

  if (layout === 'split') {
    return <SplitLayout {...props} railInk={railInk} headlineInk={headlineInk} />
  }
  if (layout === 'plaque') {
    return <PlaqueLayout {...props} headlineInk={headlineInk} />
  }
  return <StatLayout {...props} headlineInk={headlineInk} />
}
